import { useRecordContext } from "ra-core";
import { useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Settings2 } from "lucide-react";

import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin/delete-button";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../root/ConfigurationContext";
import { defaultCompanySidebarModules } from "../root/defaultConfiguration";
import { CustomFieldsDisplay } from "../misc/CustomFields";
import { QuickAddProperty } from "../misc/QuickAddProperty";
import { TagsListEdit } from "../contacts/TagsListEdit";
import type { Company, SidebarModulePref } from "../types";
import {
  AdditionalInfo,
  AddressInfo,
  CompanyInfo,
  ContextInfo,
} from "./CompanyAside";
import { CompanyHierarchy } from "./CompanyHierarchy";

/**
 * Left about rail of the company record, built from configurable modules -
 * reorder or hide sections per tenant (HubSpot's "customize the left
 * sidebar"). Preferences persist in tenant configuration.
 */

const MODULE_DEFS: Record<
  string,
  { label: string; render: (record: Company) => React.ReactNode }
> = {
  info: { label: "Contact info", render: (r) => <CompanyInfo record={r} /> },
  tags: {
    label: "Tags",
    render: () => <TagsListEdit resource="companies" />,
  },
  hierarchy: { label: "Company hierarchy", render: () => <CompanyHierarchy /> },
  context: { label: "Context", render: (r) => <ContextInfo record={r} /> },
  address: { label: "Address", render: (r) => <AddressInfo record={r} /> },
  additional: {
    label: "Additional info",
    render: (r) => <AdditionalInfo record={r} />,
  },
  custom: {
    label: "Custom properties",
    render: () => (
      <div>
        <CustomFieldsDisplay object="companies" />
        <div className="-ml-2 mt-1">
          <QuickAddProperty object="companies" />
        </div>
      </div>
    ),
  },
};

export const CompanyAboutRail = () => {
  const record = useRecordContext<Company>();
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const [customizing, setCustomizing] = useState(false);

  if (!record) return null;

  // Merge stored prefs with defaults so new modules appear automatically
  const stored = config.companySidebarModules ?? [];
  const modules: SidebarModulePref[] = [
    ...stored.filter((m) => MODULE_DEFS[m.key]),
    ...defaultCompanySidebarModules.filter(
      (d) => !stored.some((m) => m.key === d.key),
    ),
  ];

  const saveModules = (next: SidebarModulePref[]) =>
    updateConfig({ ...config, companySidebarModules: next });

  const move = (index: number, dir: -1 | 1) => {
    const next = [...modules];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    saveModules(next);
  };

  const toggleHidden = (key: string) =>
    saveModules(
      modules.map((m) => (m.key === key ? { ...m, hidden: !m.hidden } : m)),
    );

  return (
    <div className="hidden lg:flex flex-col gap-4 text-sm">
      <Card>
        <CardContent className="px-4 flex flex-col gap-2">
          <div className="flex items-center gap-1 -ml-1">
            <EditButton label="resources.companies.action.edit" />
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs text-muted-foreground ml-auto"
              onClick={() => setCustomizing((v) => !v)}
            >
              <Settings2 className="w-3.5 h-3.5" />
              {customizing ? "Done" : "Customize"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {customizing && (
        <Card>
          <CardContent className="px-4 flex flex-col">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1">
              Sidebar modules
            </p>
            {modules.map((m, i) => (
              <div
                key={m.key}
                className="flex items-center gap-1 py-1 border-t border-border first:border-t-0"
              >
                <span
                  className={m.hidden ? "text-muted-foreground/50" : undefined}
                >
                  {MODULE_DEFS[m.key]!.label}
                </span>
                <span className="ml-auto flex items-center">
                  <Button variant="ghost" size="icon" className="w-6 h-6" aria-label={`Move ${MODULE_DEFS[m.key]!.label} up`} onClick={() => move(i, -1)}>
                    <ArrowUp className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="w-6 h-6" aria-label={`Move ${MODULE_DEFS[m.key]!.label} down`} onClick={() => move(i, 1)}>
                    <ArrowDown className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="w-6 h-6" aria-label={`Toggle ${MODULE_DEFS[m.key]!.label}`} onClick={() => toggleHidden(m.key)}>
                    {m.hidden ? (
                      <EyeOff className="w-3 h-3" />
                    ) : (
                      <Eye className="w-3 h-3" />
                    )}
                  </Button>
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="px-4 flex flex-col gap-1">
          {modules
            .filter((m) => !m.hidden)
            .map((m) => (
              <div key={m.key}>{MODULE_DEFS[m.key]!.render(record)}</div>
            ))}
          <div className="mt-4 pt-4 border-t flex flex-col gap-2 items-start">
            <DeleteButton
              className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
